/* eslint-disable
  camelcase
 */
import React from 'react';
import * as uuid from 'uuid';

import { makeContextCacher } from 'signer-app/utils/legacy-context-utilities';
import {
  useSignatureModalContext,
  SignatureModalContextProvider,
  SignatureModalContext,
} from 'signer-app/signature-modal/signature-modal-context/context';
import hsFetch, { getCSRFToken } from 'signer-app/utils/hs-fetch';

import CONSTANTS from 'signer-app/signature-modal/constants';
import { Tab } from 'signer-app/signature-modal/modal';
import { SignatureTypeCode } from 'signer-app/types/signature-types';
import { SigData } from 'signer-app/context/signer-app-client/signature';
import invariant from 'invariant';
import { UnreachableError } from 'signer-app/utils/unreachable';

export type OnSignatureDataType =
  | OnSignatureUpload
  | OnTypedSignature
  | OnCanvasSignature;
type OnCanvasSignature = {
  create_type_code: 'C';
  type_code: SignatureTypeCode;
  signature: {
    image: string;
    is_vml?: boolean;
  };
};
type OnTypedSignature = {
  create_type_code: 'T';
  type_code: SignatureTypeCode;
  text: string;
  type?: SignatureTypeCode;
  signature: {
    image: string;
    text?: string;
    font_family?: string;
    is_vml?: boolean;
  };
};

type OnSignatureUpload = {
  create_type_code: 'U';
  type_code: SignatureTypeCode;
  guid: string;
  is_cropped: boolean;
  threshold: number;
  degrees: number;
  width: number;
  height: number;
  x: number;
  y: number;
  signature: {
    image: string;
    text?: string;
    font_family?: string;
    is_vml?: boolean;
  };
};

/**
 * <SignatureProviderIntercept is used when we render previews so that the modal
 * can't really interact with the backend but is otherwise still functional.
 *
 * @AppExplorer https://miro.com/app/board/uXjVPXskloA=/?moveToWidget=3458764535230714471&cot=14
 */
const SignatureProviderIntercept = (props: any) => {
  const context = useSignatureModalContext();

  const contextCache = makeContextCacher() as <T>(context: T) => T;
  const [selectedTab, setSelectedTab] = React.useState<Tab>(CONSTANTS.TAB_DRAW);
  const [savedSignatures, setSavedSignatures] = React.useState<SigData[]>([]);
  const [savedInitials, setSavedInitials] = React.useState<SigData[]>([]);

  const onSavedSignatureRemove = () => {};
  const onUploadSignature = async () => {
    return {} as any;
  };
  const onUploadSignatureAsync = async () => {
    return {} as any;
  };
  const clearUploadedSignature = () => {};

  const onTabSelect = (tab: Tab) => setSelectedTab(tab);

  /**
   * Fetches data URI for typed-in signatures without saving to DB,
   * or returns the locally-generated data URI.
   */
  const upload = async (sigData: OnSignatureDataType) => {
    const csrfToken = getCSRFToken();
    if (sigData.create_type_code === CONSTANTS.SIGNATURE_TYPE_CANVAS) {
      return {
        guid: uuid.v4(),
        image: sigData.signature.image,
      };
    }

    invariant(
      sigData.create_type_code === CONSTANTS.SIGNATURE_TYPE_TYPED,
      'Invalid create type code',
    );

    const data = {
      csrf_token: csrfToken,
      text: sigData.signature.text,
      font_family: sigData.signature.font_family,
    };

    try {
      const response = await hsFetch('/signature/generate', {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        method: 'POST',
        body: JSON.stringify(data),
      });

      invariant(response.ok, 'Failed to generate temporary signature');

      const { image } = await response.json();
      return {
        image,
        guid: uuid.v4(),
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      throw e;
    }
  };

  const createNewSignature: SignatureModalContext['createNewSignature'] = (
    data,
  ) => {
    return upload(data).then(({ image, guid }) => {
      let signature: SigData;
      if (data.create_type_code === 'U') {
        signature = {
          guid,
          signature: { image },
          type: data.type_code,
          type_code: data.type_code,
          create_type_code: data.create_type_code,
          x: data.x,
          y: data.y,
          width: data.width,
          height: data.height,
        };
      } else if (data.create_type_code === 'C') {
        signature = {
          guid,
          signature: { image },
          type: data.type_code,
          type_code: data.type_code,
          create_type_code: data.create_type_code,
          x: 0, // I'm not sure these are used anywhere
          y: 0,
          width: 0,
          height: 0,
        };
      } else if (data.create_type_code === 'T') {
        signature = {
          guid,
          signature: { image },
          type: data.type_code,
          type_code: data.type_code,
          create_type_code: data.create_type_code,
          x: 0, // I'm not sure these are used anywhere
          y: 0,
          width: 0,
          height: 0,
        };
      } else {
        throw new UnreachableError(data);
      }

      if (data.type_code === 'I') {
        setSavedInitials((s) => [...s, signature]);
      } else {
        setSavedSignatures((s) => [...s, signature]);
      }
      return signature;
    });
  };

  const getSignatureUrl = (field: { guid: string }) => {
    let signature = savedSignatures.find((sig) => sig.guid === field.guid);
    signature ??= savedInitials.find((sig) => sig.guid === field.guid);

    if (signature?.signature) {
      return signature.signature.image;
    }
    return context.getSignatureUrl(field);
  };

  const enabledTabs = [
    CONSTANTS.TAB_DRAW,
    CONSTANTS.TAB_TYPE,
    CONSTANTS.TAB_ERROR,
  ];

  const value: SignatureModalContext = contextCache({
    ...context,
    savedSignatures,
    savedInitials,
    canInsertEverywhere: false,
    onSavedSignatureRemove,
    onUploadSignature,
    onUploadSignatureAsync,
    clearUploadedSignature,
    createNewSignature,
    getSignatureUrl,
    upload,
    onTabSelect,
    selectedTab,
    enabledTabs,
  });

  return (
    <SignatureModalContextProvider value={value}>
      {props.children}
    </SignatureModalContextProvider>
  );
};

export default SignatureProviderIntercept;
